import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, Chip, CircularProgress, Grid, alpha, useTheme,
} from '@mui/material';
import EventBusyIcon from '@mui/icons-material/EventBusyRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircleRounded';
import CancelIcon from '@mui/icons-material/CancelRounded';
import PendingIcon from '@mui/icons-material/PendingRounded';
import { motion } from 'framer-motion';
import { getAttendanceHistory } from '../api/studentApi';
import StatCard from '../components/StatCard';
import EmptyState from '../components/EmptyState';
import { tokens } from '../theme/tokens';
import { staggerContainer, fadeInUp } from '../utils/motion';

const MotionTableRow = motion(TableRow);
const MotionPaper = motion(Paper);

const statusStyle = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'present') return { color: tokens.mint, icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> };
  if (s === 'absent') return { color: tokens.coral, icon: <CancelIcon sx={{ fontSize: 16 }} /> };
  if (s === 'leave' || s === 'half day' || s === 'halfday') return { color: tokens.amber, icon: <PendingIcon sx={{ fontSize: 16 }} /> };
  return { color: tokens.inkSoft, icon: null };
};

// Picks an icon/color for a summary metric based on its key name, since the
// exact shape of `summary` from the API isn't fixed.
const summaryMeta = (key) => {
  const k = key.toLowerCase();
  if (k.includes('present')) return { color: tokens.mint, icon: <CheckCircleIcon /> };
  if (k.includes('absent')) return { color: tokens.coral, icon: <EventBusyIcon /> };
  if (k.includes('leave') || k.includes('half')) return { color: tokens.amber, icon: <PendingIcon /> };
  return { color: tokens.indigo, icon: <CheckCircleIcon /> };
};

const Attendance = () => {
  const theme = useTheme();
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(true);

  // NOTE: response shape assumed as { summary: {...}, records: [...] }.
  // Adjust the destructuring below once the real payload is confirmed.
  const fetchData = async (start, end) => {
    setLoading(true);
    try {
      const res = await getAttendanceHistory(undefined, start, end);
      setSummary(res.data?.summary || null);
      setRecords(res.data?.records || res.data?.attendance || (Array.isArray(res.data) ? res.data : []));
    } catch (err) {
      console.error('Failed to load attendance', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleFilter = () => fetchData(startDate, endDate);
  const handleReset = () => { setStartDate(''); setEndDate(''); fetchData(); };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>My attendance</Typography>

      {summary && (
        <Grid container spacing={2} sx={{ mb: 2.5 }} component={motion.div} variants={staggerContainer} initial="hidden" animate="show">
          {Object.entries(summary).map(([key, value]) => {
            const meta = summaryMeta(key);
            return (
              <Grid item xs={6} sm={3} key={key}>
                <StatCard
                  icon={meta.icon}
                  label={key.replace(/_/g, ' ')}
                  value={value}
                  color={meta.color}
                  dense
                />
              </Grid>
            );
          })}
        </Grid>
      )}

      <MotionPaper
        variants={fadeInUp} initial="hidden" animate="show"
        elevation={0}
        sx={{
          display: 'flex', gap: 2, mb: 2.5, alignItems: 'center', flexWrap: 'wrap',
          p: 2, borderRadius: '16px', border: '1px solid', borderColor: 'divider',
        }}
      >
        <TextField
          label="From" type="date" size="small" InputLabelProps={{ shrink: true }}
          value={startDate} onChange={(e) => setStartDate(e.target.value)}
        />
        <TextField
          label="To" type="date" size="small" InputLabelProps={{ shrink: true }}
          value={endDate} onChange={(e) => setEndDate(e.target.value)}
        />
        <Button variant="contained" onClick={handleFilter}>Filter</Button>
        <Button variant="outlined" onClick={handleReset}>Reset</Button>
      </MotionPaper>

      <Paper elevation={0} sx={{ borderRadius: '18px', overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: alpha(theme.palette.primary.main, 0.05) } }}>
                <TableCell>S.No</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Remarks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 5 }}><CircularProgress size={22} /></TableCell></TableRow>
              ) : records.length > 0 ? (
                records.map((r, i) => {
                  const meta = statusStyle(r.status);
                  return (
                    <MotionTableRow
                      key={r.id || i}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: Math.min(i * 0.03, 0.6), duration: 0.3 }}
                      sx={{ '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) } }}
                    >
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{r.date || r.attendance_date}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          icon={meta.icon || undefined}
                          label={r.status}
                          sx={{ backgroundColor: alpha(meta.color, 0.14), color: meta.color, textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>{r.remarks || '—'}</TableCell>
                    </MotionTableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={4} sx={{ border: 'none', py: 4 }}>
                    <EmptyState
                      icon={<EventBusyIcon />}
                      title="No attendance records found"
                      subtitle="Try a different date range, or check back after your next class."
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default Attendance;
